import * as React from 'react';
import { useRef, useImperativeHandle, forwardRef, useCallback, useState, useLayoutEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

export interface VirtualizedItemListHandle {
    scrollToIndex: (index: number, options?: { align?: 'start' | 'center' | 'end' | 'auto' }) => void;
}

interface VirtualizedItemListProps {
    count: number;
    scrollContainerRef: React.RefObject<HTMLElement>;
    renderItem: (index: number) => React.ReactNode;
    estimateSize?: number;
    overscan?: number;
    gap?: number;
    className?: string;
}

const VirtualizedItemList = forwardRef<VirtualizedItemListHandle, VirtualizedItemListProps>(
    ({ count, scrollContainerRef, renderItem, estimateSize = 120, overscan = 5, gap = 0, className }, ref) => {
        const listRef = useRef<HTMLDivElement>(null);
        const measurementCache = useRef<Record<number, number>>({});

        // Measure the offset from the scroll container's top to the list container's top.
        // This tells the virtualizer where the list actually starts inside the scroll parent.
        const [scrollMargin, setScrollMargin] = useState(0);
        useLayoutEffect(() => {
            const listEl = listRef.current;
            const scrollEl = scrollContainerRef.current;
            if (!listEl || !scrollEl) return;

            const measure = () => {
                const listRect = listEl.getBoundingClientRect();
                const scrollRect = scrollEl.getBoundingClientRect();
                setScrollMargin(listRect.top - scrollRect.top + scrollEl.scrollTop);
            };

            measure();

            const observer = new ResizeObserver(measure);
            observer.observe(scrollEl);
            observer.observe(listEl);
            return () => observer.disconnect();
        }, [scrollContainerRef]);

        const virtualizer = useVirtualizer({
            count,
            getScrollElement: () => scrollContainerRef.current,
            estimateSize: useCallback(
                (index: number) => measurementCache.current[index] ?? estimateSize,
                [estimateSize]
            ),
            overscan,
            gap,
            scrollMargin,
            measureElement: (el) => {
                if (!el) return undefined;
                const height = el.getBoundingClientRect().height;
                const index = Number((el as HTMLElement).dataset.index);
                if (!isNaN(index)) {
                    measurementCache.current[index] = height;
                }
                return height;
            },
        });

        useImperativeHandle(
            ref,
            () => ({
                scrollToIndex: (index: number, options?: { align?: 'start' | 'center' | 'end' | 'auto' }) => {
                    virtualizer.scrollToIndex(index, {
                        align: options?.align ?? 'start',
                        behavior: 'instant',
                    });
                },
            }),
            [virtualizer]
        );

        const virtualItems = virtualizer.getVirtualItems();

        return (
            <div
                ref={listRef}
                className={className}
                style={{
                    height: virtualizer.getTotalSize(),
                    position: 'relative',
                }}
            >
                {virtualItems.map((virtualRow) => (
                    <div
                        key={virtualRow.key}
                        data-index={virtualRow.index}
                        ref={virtualizer.measureElement}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            minWidth: '100%',
                            transform: `translateY(${virtualRow.start - scrollMargin}px)`,
                        }}
                    >
                        {renderItem(virtualRow.index)}
                    </div>
                ))}
            </div>
        );
    }
);

VirtualizedItemList.displayName = 'VirtualizedItemList';

export default VirtualizedItemList;
